import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialPayment, Payment } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { PaymentsListLoading } from '../components/loading/PaymentsListLoading'
import { createPayment, updatePayment } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage } from 'react-intl'
import axios from 'axios'
import Select from 'react-select'
import { PaymentMethod } from '../../../paymentmethod/paymentmethods-list/core/_models'
import { Currency } from '../../../language/languages-list/core/_models'
import { Reseller } from '../../../reseller/resellers-list/core/_models'

type Props = {
  isPaymentLoading: boolean
  payment: Payment
}

type ResellerOption = {
  value: any
  label: any
}
const editPaymentSchema = Yup.object().shape({
  reseller_id: Yup.string().required('Reseller is required'),
  payment_method_id: Yup.string().required('Payment method is required'),
  amount: Yup.number().required('Amount is required').min(0.01, 'Amount must be positive'),
  currency_id: Yup.string().required('Currency is required'),
  status: Yup.string().oneOf(['completed', 'pending', 'failed']).required('Status is required'),
  notes: Yup.string().nullable(),
  payment_date: Yup.date().required('Payment date is required'),
})

const PaymentEditModalForm: FC<Props> = ({ payment, isPaymentLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()
  const [resellerOptions, setResellerOptions] = useState<ResellerOption[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [resellers, setResellers] = useState<Reseller[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [isCurrencyDisabled, setIsCurrencyDisabled] = useState(false)

  useEffect(() => {
    axios.get(`${API_URL}/admin/paymentmethods`).then((response) => {
      if (response.data.success) {
        setPaymentMethods(response.data.data.paymentmethods)
      }
    }).catch((error) => console.error('Error fetching payment methods:', error))

    axios.get(`${API_URL}/admin/resellers`).then((response) => {
      if (response.data.success) {
        const options: ResellerOption[] = response.data.data.resellers.map((reseller: Reseller) => ({
          value: reseller.id,
          label: reseller.reseller_name,
        }))
        setResellerOptions(options)
      }
    }).catch((error) => console.error('Error fetching resellers:', error))

    axios.get(`${API_URL}/admin/currencies`).then((response) => {
      if (response.data.success) {
        setCurrencies(response.data.data.currencies)
      }
    }).catch((error) => console.error('Error fetching currencies:', error))
  }, [API_URL])

  const handleResellerChange = async (selectedOption: any) => {
    const resellerId = selectedOption?.value
    formik.setFieldValue('reseller_id', resellerId)

    try {
      const resellerResponse = await axios.get(`${API_URL}/admin/resellers/${resellerId}`)
      const resellerData = resellerResponse.data?.data?.reseller

      if (resellerData?.currency.id) {
        formik.setFieldValue('currency_id', resellerData.currency.id)
        setIsCurrencyDisabled(true)
      } else {
        setIsCurrencyDisabled(false)
      }
    } catch (error) {
      console.error('Error fetching reseller currency preference:', error)
    }
  }

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: {
      ...payment,
      reseller_id: payment?.reseller_id || initialPayment?.reseller_id,
      payment_method_id: payment.payment_method_id || initialPayment.payment_method_id,
      amount: payment.amount || initialPayment.amount,
      currency_id: payment.currency_id || initialPayment.currency_id,
      status: payment.status || initialPayment.status,
      notes: payment.notes || initialPayment.notes,
      payment_date: payment.payment_date
      ? payment.payment_date.split(' ')[0] // Extracts 'YYYY-MM-DD' from 'YYYY-MM-DD HH:mm:ss'
      : initialPayment.payment_date,    },
    validationSchema: editPaymentSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updatePayment(values)
        } else {
          await createPayment(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  

  return (
    <>
      <form id='kt_modal_add_payment_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column scroll-y me-n7 pe-7' id='kt_modal_add_payment_scroll' data-kt-scroll='true' data-kt-scroll-activate='{default: false, lg: true}' data-kt-scroll-max-height='auto' data-kt-scroll-dependencies='#kt_modal_add_payment_header' data-kt-scroll-wrappers='#kt_modal_add_payment_scroll' data-kt-scroll-offset='300px'>

          {/* Split into two columns */}
          <div className="row">
            {/* Reseller Select */}
            <div className='col-lg-6 fv-row mb-7'>
  <label className='required fw-bold fs-6 mb-2'>
    <FormattedMessage id='PAYMENT.FORM.INPUT.RESELLER' defaultMessage='Reseller' />
  </label>
  <Select
    options={resellerOptions}
    onChange={(handleResellerChange)}
    isDisabled={formik.isSubmitting || isPaymentLoading}
    placeholder="Select a reseller"
    className={clsx(
      { 'is-invalid': formik.touched.reseller_id && formik.errors.reseller_id },
      { 'is-valid': formik.touched.reseller_id && !formik.errors.reseller_id }
    )}
    value={resellerOptions.find(option => option.value == formik.values.reseller_id)}
    styles={{
      control: (provided) => ({
        ...provided,
        padding: '0.265rem 0.75rem', // Match padding to other input fields
        borderColor: '#E4E6EF', // Optional: match the border color
        borderRadius: '0.475rem', // Optional: adjust border radius for consistency
        minHeight: 'calc(1.5em + 0.75rem + 2px)' // Optional: adjust height to match other inputs
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: '0 0 0 0' // Optional: adjust inner padding to ensure text alignment
      })
    }}
  />
</div>


            {/* Payment Method */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PAYMENT.FORM.INPUT.PAYMENTMETHOD' defaultMessage='Payment Method' /></label>
              <select
                {...formik.getFieldProps('payment_method_id')}
                className={clsx(
                  'form-select form-control',
                  { 'is-invalid': formik.touched.payment_method_id && formik.errors.payment_method_id },
                  { 'is-valid': formik.touched.payment_method_id && !formik.errors.payment_method_id }
                )}
                disabled={formik.isSubmitting || isPaymentLoading}
              >
                <option value=''>Select</option>
                {paymentMethods.map((method) => (
                  <option key={method.id} value={method.id}>{method.method_name}</option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PAYMENT.FORM.INPUT.AMOUNT' defaultMessage='Amount' /></label>
              <input
                {...formik.getFieldProps('amount')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.amount && formik.errors.amount },
                  { 'is-valid': formik.touched.amount && !formik.errors.amount }
                )}
                type='text'
                autoComplete='off'
                disabled={formik.isSubmitting || isPaymentLoading}
              />
            </div>

            {/* Currency */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PAYMENT.FORM.INPUT.CURRENCY' defaultMessage='Currency' /></label>
              <select
                {...formik.getFieldProps('currency_id')}
                className='form-select form-control'
                disabled={!currencies.length || isCurrencyDisabled}
              >
                <option value='' disabled>Select</option>
                {currencies.map((currency) => (
                  <option key={currency.id} value={currency.id} selected={currency.id == formik.values.currency_id}>
                    {currency.name} - {currency.code}
                  </option>
                ))}
              </select>
            </div>


            {/* Notes */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='PAYMENT.FORM.INPUT.NOTES' defaultMessage='Notes' /></label>
              <textarea
                {...formik.getFieldProps('notes')}
                className='form-control form-control-solid'
                placeholder='Enter notes'
                disabled={formik.isSubmitting || isPaymentLoading}
              />
            </div>

            {/* Payment Date */}
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PAYMENT.FORM.INPUT.PAYMENTDATE' defaultMessage='Payment Date' /></label>
              <input
                {...formik.getFieldProps('payment_date')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.payment_date && formik.errors.payment_date },
                  { 'is-valid': formik.touched.payment_date && !formik.errors.payment_date }
                )}
                type='date'
                autoComplete='off'
                disabled={formik.isSubmitting || isPaymentLoading}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            disabled={formik.isSubmitting || isPaymentLoading}
          >
            <FormattedMessage id='FORM.GENERAL.DISCARD' defaultMessage='Discard' />
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            disabled={isPaymentLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'>
              <FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='Submit' />
            </span>
            {formik.isSubmitting && (
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isPaymentLoading && <PaymentsListLoading />}
    </>
  )
}

export { PaymentEditModalForm }
